import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  GetConversationDetailsRequest,
  GetConversationListRequest,
  GetConversationListResponse,
  UpdateConversationTitleRequest,
  UpdateConversationTitleResponse,
  GetCitationResponse,
} from "@amzn/atlas-chat-react-library";
import { AtlasChatbotProps } from "@amzn/atlas-chat-react-library";
import { GaiaConstants } from "utils";
import { ChatAuthTokenResponse } from "pages/chatbot/definitions";

interface AtlasChatServiceParams {
  appId: string;
  conversationId?: string;
  messageId?: string;
  title?: string;
  liked?: boolean;
  feedback?: string;
  citationId?: string;
}

const dynamicBaseQuery = (args: any, api: any, extraOptions: any) => {
  const state = api.getState() as any;
  const atlasApiUrl = state.settings?.endpoints?.atlasApiUrl;

  const baseQuery = fetchBaseQuery({
    baseUrl: `${atlasApiUrl}/api/v1/apps/`,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as any;
      if (state.auth?.jwtToken) {
        headers.set("authorization", state.auth.jwtToken);
      }
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const atlasChatServiceApi = createApi({
  reducerPath: "atlasChatServiceApi",
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    getChatAuth: builder.query<any, AtlasChatServiceParams>({
      query: (params: AtlasChatServiceParams) => ({
        url: `/${params.appId}/chatAuth`,
      }),
      transformResponse: (response: ChatAuthTokenResponse) => {
        return response.token;
      },
    }),
    getConversations: builder.query<
      AtlasChatbotProps.GroupedConversation,
      GetConversationListRequest
    >({
      query: (params: GetConversationListRequest) => ({
        url: `/${params.appId}/conversations`,
        params: {
          user: params.user,
          startDate: params.startDate,
          endDate: params.endDate,
          limit: GaiaConstants.ATLAS_CHATBOT_CONVERSATIONS_LIMIT,
          ...(params.paginationToken && {
            paginationToken: params.paginationToken,
          }),
        },
      }),
      transformResponse: (response: GetConversationListResponse) => {
        return {
          conversations: response.conversations || [],
          paginationToken: response.paginationToken,
        } as AtlasChatbotProps.GroupedConversation;
      },
    }),
    getConversationDetails: builder.query<
      AtlasChatbotProps.Conversation,
      GetConversationDetailsRequest
    >({
      query: (params) => ({
        url: `/${params.appId}/conversations/${params.conversationId}/messages`,
        params: {
          limit: GaiaConstants.ATLAS_CHATBOT_MESSAGES_LIMIT,
          ...(params.paginationToken && {
            paginationToken: params.paginationToken,
          }),
        },
      }),
      transformResponse: (
        response: AtlasChatbotProps.Conversation,
        _,
        args
      ) => {
        response.messages?.map(
          (value: AtlasChatbotProps.ConversationMessage) =>
            (value.conversationId = args.conversationId)
        );
        return response;
      },
    }),
    getCitation: builder.query<string, any>({
      query: (params: AtlasChatServiceParams) => ({
        url: `/${params.appId}/conversations/${params.conversationId}/messages/${params.messageId}/citations/${params.citationId}/download-url`,
      }),
      transformResponse: (response: GetCitationResponse) => {
        return response.downloadUrl;
      },
    }),
    saveFeedback: builder.mutation<any, any>({
      query: (params: AtlasChatServiceParams) => ({
        url: `${params.appId}/conversations/${params.conversationId}/messages/${params.messageId}/feedback`,
        body: {
          isPositive: params.liked,
          comment: params.feedback,
        },
        method: "POST",
      }),
    }),
    saveTitle: builder.mutation<
      UpdateConversationTitleResponse,
      UpdateConversationTitleRequest
    >({
      query: (params: AtlasChatServiceParams) => ({
        url: `${params.appId}/conversations/${params.conversationId}/`,
        body: {
          title: params.title,
        },
        method: "PATCH",
      }),
    }),
    deleteConversation: builder.mutation<any, any>({
      query: (params: AtlasChatServiceParams) => ({
        url: `${params.appId}/conversations/${params.conversationId}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetChatAuthQuery,
  useGetConversationsQuery,
  useGetConversationDetailsQuery,
  useGetCitationQuery,
  useSaveFeedbackMutation,
  useSaveTitleMutation,
  useDeleteConversationMutation,
} = atlasChatServiceApi;
